<template>
  <Component
    :is="tag"
    ref="button"
    :class="
      removeClassExtras(`
      ${component.size}
      ${component.state}
      ${full ? 'w-full justify-center' : ''}
      ${to ? 'inline-flex' : 'flex'}
    `)
    "
    class="group empty:hidden items-center justify-center cursor-pointer"
    v-bind="attributes"
    @click="handleClick"
  >
    <slot
      v-if="hasIconLeft || $slots['icon-left']"
      name="icon-left"
      :icon-class="iconClass"
    >
      <V2IconsArrow
        ref="icon"
        :class="iconClass"
        :width="component.arrowSize.width"
        :height="component.arrowSize.height"
        :direction="iconDirection"
      />
    </slot>

    <slot v-if="$slots.default" />

    <slot
      v-if="hasIconRight || $slots['icon-right']"
      name="icon-right"
      :icon-class="iconClass"
    >
      <V2IconsArrow
        ref="icon"
        :class="iconClass"
        :width="component.arrowSize.width"
        :height="component.arrowSize.height"
        :direction="iconDirection"
      />
    </slot>
  </Component>
</template>

<script setup>
import _isEmpty from 'underscore/cjs/isEmpty.js'

const { $tracking } = useNuxtApp()
const $attrs = useAttrs()
const $router = useRouter()

defineOptions({
  name: 'V2AtomsButton'
})

const props = defineProps({
  theme: {
    type: String,
    default: 'primary',
    validator: value => ['primary', 'secondary', 'tertiary'].includes(value)
  },
  size: {
    type: String,
    default: 'large',
    validator: value => ['x-small', 'small', 'base', 'large'].includes(value)
  },
  type: {
    type: String,
    default: 'button',
    validator: value => ['button', 'submit'].includes(value)
  },
  inverted: {
    type: Boolean,
    default: false
  },
  hasIconRight: {
    type: Boolean,
    default: false
  },
  hasIconLeft: {
    type: Boolean,
    default: false
  },
  iconDirection: {
    type: String,
    default: 'right',
    validator: value => ['left', 'right', 'bottom'].includes(value)
  },
  iconSize: {
    type: Object,
    default: () => ({})
  },
  full: {
    type: Boolean,
    default: false
  },
  enableTracking: {
    type: Boolean,
    default: true
  },
  to: {
    type: String,
    default: ''
  },
  crossApp: {
    type: Boolean,
    default: false
  },
  trackingAdditionalParams: {
    type: Object,
    default: () => {
      return {}
    }
  }
})

const tag = computed(() => {
  const to = props.to

  if (
    (to &&
      (to.includes('tel:') ||
        to.includes('mailto:') ||
        to.includes('http') ||
        to.startsWith('#') ||
        to.includes('www'))) ||
    props.crossApp
  ) {
    return 'a'
  }

  return to ? 'nuxt-link' : 'button'
})

const iconClass = computed(() => {
  const map = {
    primary: !props.inverted
      ? `
      fill-semantic-primary-on-default
      group-hover:fill-semantic-primary-on-light
      group-active:fill-semantic-primary-on-light
      `
      : `
      fill-semantic-inverse-on-primary
      group-hover:fill-semantic-inverse-on-primary-variant
      group-active:fill-semantic-inverse-on-primary-variant
      `,
    secondary: !props.inverted
      ? `
      fill-semantic-secondary-default
      group-active:fill-semantic-secondary-on-light
    `
      : `
      fill-semantic-inverse-primary
      group-active:fill-semantic-inverse-on-primary-variant
    `,
    tertiary: !props.inverted
      ? `
        fill-semantic-secondary-default
        group-hover:fill-semantic-secondary-variant-light
        group-active:fill-semantic-secondary-variant-highlight
      `
      : `
        fill-semantic-inverse-primary 
        group-hover:fill-semantic-inverse-primary-variant 
        group-active:fill-semantic-inverse-primary-variant-light
      `
  }

  return !isDisabled()
    ? map[props.theme]
    : ' fill-semantic-states-disabled-on-default'
})

const attributes = computed(() => {
  const to = props.to

  const attrs = {
    ...((!_isEmpty($attrs) && {
      ...$attrs,
      type: props.type
    }) ||
      {})
  }

  delete attrs.class
  delete attrs.style

  if (tag.value === 'a') {
    attrs.href = to
  } else if (tag.value === 'nuxt-link') {
    /**
     * set to empty string because <nuxt-link> ignores @click handler
     * `to` value added in handleClick method
     * `target` value is added in handleClick method
     */
    attrs.to = ''
    attrs.href = props.to
    attrs.target = ''
  } else if (tag.value === 'button') {
    attrs.type = props.type
  }

  return attrs
})

const isDisabled = () => {
  return (
    $attrs?.disabled === '' ||
    $attrs?.disabled ||
    $attrs?.['aria-disabled'] === '' ||
    $attrs?.['aria-disabled']
  )
}

const component = computed(() => {
  const state = (() => {
    if (!props.theme) {
      return ''
    }

    // Has conditions for Disabled
    const common = `
      gap-2
      font-semibold
      rounded-full shadow-none
      focus:ring-4 focus:ring-base-lavender-200
      disabled:cursor disabled:border-none
      disabled:bg-semantic-states-disabled-default
      disabled:text-semantic-states-disabled-on-default
    `
    const map = {
      primary: !props.inverted
        ? `
        text-semantic-primary-on-default
        bg-semantic-primary-default
        hover:text-semantic-primary-on-light hover:bg-semantic-primary-variant-light
        active:text-semantic-primary-on-light active:bg-semantic-primary-variant-highlight
      `
        : `
        text-semantic-inverse-on-primary
        bg-semantic-inverse-primary
        hover:text-semantic-inverse-on-primary-variant hover:bg-semantic-inverse-primary-variant
        active:text-semantic-inverse-on-primary-variant active:bg-semantic-inverse-primary-variant-light
      `,
      secondary: !props.inverted
        ? `
        border border-semantic-secondary-default
        text-semantic-secondary-default
        bg-semantic-surface-default
        hover:bg-semantic-surface-container-high
        active:text-semantic-secondary-on-light active:bg-semantic-secondary-variant-highlight
      `
        : `
        border border-semantic-inverse-primary
        text-semantic-inverse-primary
        bg-transparent
        hover:bg-semantic-inverse-container-lowest
        active:text-semantic-inverse-on-primary-variant active:bg-semantic-inverse-primary-variant-light
      `,
      tertiary: !props.inverted
        ? `
        underline py-1
        text-semantic-secondary-default
        bg-transparent
        focus:bg-semantic-surface-default
        hover:text-semantic-secondary-variant-light
        active:text-semantic-secondary-variant-highlight
      `
        : `
        underline py-1
        text-semantic-inverse-primary
        bg-transparent
        hover:text-semantic-inverse-primary
        active:text-semantic-inverse-primary-variant-light
        disabled:text-semantic-states-disabled-on-default
      `
    }

    return removeClassExtras(map[props.theme] + ` ${common}`)
  })()

  const size = (() => {
    if (!props.size || !props.theme) {
      return ''
    }

    const map = {
      'x-small': 'text-xs',
      small: 'text-sm',
      base: 'text-base',
      large: 'text-lg leading-[1.625rem]'
    }

    const px = {
      base: ' px-6 py-2',
      large: ' px-6 py-3',
      small: ' px-6 py-2',
      'x-small': ' px-3 py-1'
    }

    if (props.hasIconRight || props.hasIconLeft) {
      px.base = ' px-6'
      px.small = ' px-4'
      px.large = ' px-6'
      px['x-small'] = ' px-4'
    }

    if (props.theme !== 'tertiary') {
      map['x-small'] += ` h-8 ${px['x-small']}`
      map.small += ` h-8 ${px.small}`
      map.base += ` h-10 ${px.base}`
      map.large += ` h-12 ${px.large}`
    } else {
      map[props.size] += ' h-6'
    }

    return removeClassExtras(map[props.size])
  })()

  const arrowSize = (() => {
    if (!props.size || !props.theme) {
      return ''
    }

    if (!_isEmpty(props.iconSize)) {
      return props.iconSize
    }

    const map = {
      'x-small': {
        height: '15',
        width: '12'
      },
      small: {
        height: '20',
        width: '20'
      },
      base: {
        height: '24',
        width: '16'
      },
      large: {
        height: '24',
        width: '24'
      }
    }

    return map[props.size]
  })()

  return {
    state,
    size,
    arrowSize
  }
})

const button = ref(null)

onMounted(() => {
  const el = button?.value

  if (tag.value !== 'a' && el && el.getAttribute('target')) {
    el.removeAttribute('target')
  }
})

const handleClick = event => {
  try {
    if (props.enableTracking) {
      $tracking.click(event, props.to, props.trackingAdditionalParams)
    }

    if (props.to && tag.value === 'nuxt-link') {
      if ($attrs.target) {
        const link = $router.resolve(props.to)
        window.open(link.href, $attrs.target)
      } else {
        $router.push(props.to)
      }
    }
  } catch (error) {
    throw new Error(error)
  }
}
</script>
